// IndexedDB wrapper for Model A directory handle persistence + sandbox chapters

const DB_NAME = "fulkit-vault";
const DB_VERSION = 6;
const STORE = "handles";
const SANDBOX_STORE = "sandbox-chapters";
// V2 Step 11b — reconcile-on-chat-open cache window timestamp store.
// Keyed by userId. Value shape: { lastReconcileAt: number (Date.now()) }.
// 1h cache window per V2 spec Y12 — vault-rearchitecture-v2.md line 1043.
const RECONCILE_CACHE_STORE = "reconcile-cache";
// V2 Step 11c — chat-thread whisper per-folder cap store.
// Keyed by userId. Value shape: string[] (array of folder names that have
// already been whispered about). Persists across sessions; "Fired once per
// new user-created folder detected. Not nagged." per V2 spec line 125.
const WHISPERED_FOLDERS_STORE = "whispered-folders";
// V2 Step 11d — drift whisper 24h global cap store.
// Keyed by userId. Value shape: { lastDriftWhisperAt: number (Date.now()) }.
// Per V2 spec line 118: "Max one whisper per 24h per user, regardless of
// drift event count." Pave action runs unconditionally on every confident
// rename — this gates the whisper render only.
const DRIFT_WHISPER_CAP_STORE = "drift-whisper-cap";
// V2 Step 13b — per-machine identity store.
// Keyed by userId. Value shape: { machineId, friendlyName, folderName,
// connectedAt, lastSeenAt }. Single-record-per-user (this device's
// identity for this browser instance). Random UUID — NOT
// fingerprint-derived (privacy first per V2 spec line 712).
// Per V2 spec lines 712 + 348-358: cross-device awareness via Supabase
// preferences mirror is deferred to post-V2 ship (no consumer in 13b);
// IDB-only registry is sufficient for "This computer" surface.
const MACHINES_STORE = "machines";
const LEGACY_KEY = "vault-directory";

function keyFor(userId) {
  if (!userId) throw new Error("vault-idb: userId required");
  return `${LEGACY_KEY}:${userId}`;
}

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = request.result;
      // v1: handles store
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
      // v2: sandbox-chapters store (keyPath = sandboxId+chapterId)
      if (!db.objectStoreNames.contains(SANDBOX_STORE)) {
        const store = db.createObjectStore(SANDBOX_STORE, { autoIncrement: true });
        store.createIndex("sandboxId", "sandboxId", { unique: false });
      }
      // v3: reconcile-cache store (V2 Step 11b — keyed by userId)
      if (!db.objectStoreNames.contains(RECONCILE_CACHE_STORE)) {
        db.createObjectStore(RECONCILE_CACHE_STORE);
      }
      // v4: whispered-folders store (V2 Step 11c — keyed by userId,
      // value is string[] of folder names already whispered about).
      // Stale users hitting v1→v4 or v2→v4 fallthrough land here cleanly:
      // each `if (!contains)` check is independent of upgrade path.
      if (!db.objectStoreNames.contains(WHISPERED_FOLDERS_STORE)) {
        db.createObjectStore(WHISPERED_FOLDERS_STORE);
      }
      // v5: drift-whisper-cap store (V2 Step 11d — keyed by userId,
      // value is { lastDriftWhisperAt: number } per V2 spec line 118
      // 24h cap). Stale-account upgrade path: pre-2 → 5 traversal
      // hits all four `if (!contains)` checks sequentially via
      // standard onupgradeneeded fallthrough — verified safe.
      if (!db.objectStoreNames.contains(DRIFT_WHISPER_CAP_STORE)) {
        db.createObjectStore(DRIFT_WHISPER_CAP_STORE);
      }
      // v6: machines store (V2 Step 13b — keyed by userId, value is
      // { machineId, friendlyName, folderName, connectedAt, lastSeenAt }
      // per V2 spec line 712 "per-machine state in IndexedDB").
      // Single-record-per-user — this device's identity for this
      // browser instance. Random UUID generated on first vault connect.
      // Stale-account upgrade path: pre-2 → 6 traversal hits all five
      // `if (!contains)` checks sequentially.
      if (!db.objectStoreNames.contains(MACHINES_STORE)) {
        db.createObjectStore(MACHINES_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveHandle(userId, handle) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(handle, keyFor(userId));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getHandle(userId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const request = tx.objectStore(STORE).get(keyFor(userId));
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

export async function clearHandle(userId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(keyFor(userId));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Remove the pre-fix unkeyed handle. Safe to call repeatedly.
export async function clearLegacyHandle() {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(LEGACY_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    // best-effort cleanup
  }
}

// ─── Sandbox chapter persistence ─────────────────────────────

export async function saveChapter(sandboxId, chapter) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SANDBOX_STORE, "readwrite");
    tx.objectStore(SANDBOX_STORE).add({ sandboxId, ...chapter });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadChapters(sandboxId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SANDBOX_STORE, "readonly");
    const index = tx.objectStore(SANDBOX_STORE).index("sandboxId");
    const request = index.getAll(sandboxId);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

export async function clearSandbox(sandboxId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SANDBOX_STORE, "readwrite");
    const store = tx.objectStore(SANDBOX_STORE);
    const index = store.index("sandboxId");
    const request = index.openCursor(sandboxId);
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── V2 Step 11b — reconcile-on-chat-open cache window ───────
//
// Cache window mechanics per V2 spec Y12 (vault-rearchitecture-v2.md
// line 1043): "Skip directory walk if last reconcile <1h on this
// machine. Cache timestamp in IndexedDB per directoryHandle."
//
// Stored per-userId. Value shape: { lastReconcileAt: number }
// where lastReconcileAt is a Date.now() millisecond timestamp.
// Returns null if no cache entry exists (first chat-open after
// install or DB upgrade).

export async function getReconcileCache(userId) {
  if (!userId) return null;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(RECONCILE_CACHE_STORE, "readonly");
    const request = tx.objectStore(RECONCILE_CACHE_STORE).get(keyFor(userId));
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

export async function setReconcileCache(userId, data) {
  if (!userId) throw new Error("vault-idb: userId required");
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(RECONCILE_CACHE_STORE, "readwrite");
    tx.objectStore(RECONCILE_CACHE_STORE).put(data, keyFor(userId));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── V2 Step 11c — chat-thread whisper per-folder cap ─────────
//
// Tracks which user-created folder names have already been whispered
// about, so the whisper fires once per new folder per V2 spec line 125
// ("Fired once per new user-created folder detected. Not nagged.").
//
// Stored per-userId as a string[] of folder names. Not a Set on disk —
// IDB serializes plain arrays cleanly; consumers cast to Set in memory
// for O(1) lookup. Order doesn't matter; new folder names append.

export async function getWhisperedFolders(userId) {
  if (!userId) return new Set();
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(WHISPERED_FOLDERS_STORE, "readonly");
    const request = tx.objectStore(WHISPERED_FOLDERS_STORE).get(keyFor(userId));
    request.onsuccess = () => resolve(new Set(request.result || []));
    request.onerror = () => reject(request.error);
  });
}

export async function addWhisperedFolder(userId, folderName) {
  if (!userId || !folderName) throw new Error("vault-idb: userId + folderName required");
  const existing = await getWhisperedFolders(userId);
  if (existing.has(folderName)) return; // already whispered, no-op
  existing.add(folderName);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(WHISPERED_FOLDERS_STORE, "readwrite");
    tx.objectStore(WHISPERED_FOLDERS_STORE).put(Array.from(existing), keyFor(userId));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── V2 Step 11d — drift whisper 24h global cap ──────────────
//
// 24h cap mechanics per V2 spec line 118: "Max one whisper per 24h
// per user, regardless of drift event count." Pave action runs
// unconditionally on every confident rename (auto-repair is structural
// per V2 spec line 119); this store gates whisper render only.
//
// Stored per-userId. Value shape: { lastDriftWhisperAt: number } where
// lastDriftWhisperAt is a Date.now() millisecond timestamp. Returns
// null if no cap entry exists (first drift event after install or DB
// upgrade).

export async function getDriftWhisperCap(userId) {
  if (!userId) return null;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DRIFT_WHISPER_CAP_STORE, "readonly");
    const request = tx.objectStore(DRIFT_WHISPER_CAP_STORE).get(keyFor(userId));
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

export async function setDriftWhisperCap(userId, timestamp) {
  if (!userId) throw new Error("vault-idb: userId required");
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DRIFT_WHISPER_CAP_STORE, "readwrite");
    tx.objectStore(DRIFT_WHISPER_CAP_STORE).put({ lastDriftWhisperAt: timestamp }, keyFor(userId));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── V2 Step 13b — per-machine identity registry ──────────────
//
// Single-record-per-user store. machineId is a random UUID generated
// on first vault connect — represents THIS device for THIS user in
// THIS browser instance. Persists across disconnect/reconnect cycles
// (machine identity = device, not connection state). Browser cache
// clear → record gone → fresh UUID on next connect (privacy-correct
// trade-off, no fingerprint fallback).
//
// Schema:
//   { machineId: string, friendlyName: string, folderName: string,
//     connectedAt: number, lastSeenAt: number }
//
// Cross-device awareness via Supabase preferences mirror is DEFERRED
// to post-V2 ship (no consumer in 13b — catchup whisper is chat-side
// sub-feature, "Other machines" list not in V2 spec for Step 13).
// 13b is IDB-only.

export async function getMachine(userId) {
  if (!userId) return null;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(MACHINES_STORE, "readonly");
    const request = tx.objectStore(MACHINES_STORE).get(keyFor(userId));
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

export async function setMachine(userId, machineRecord) {
  if (!userId) throw new Error("vault-idb: userId required");
  if (!machineRecord?.machineId) throw new Error("vault-idb: machineId required");
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(MACHINES_STORE, "readwrite");
    tx.objectStore(MACHINES_STORE).put(machineRecord, keyFor(userId));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function clearMachine(userId) {
  if (!userId) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(MACHINES_STORE, "readwrite");
    tx.objectStore(MACHINES_STORE).delete(keyFor(userId));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Touch lastSeenAt without rewriting the rest of the record. Called
// fire-and-forget from runReconcile after successful walk (1h cache
// window throttles writes naturally). No-op if no machine record
// exists (cloud-only users / pre-13b accounts that haven't been
// backfilled yet).
export async function touchMachineLastSeen(userId, timestamp) {
  if (!userId) return;
  try {
    const existing = await getMachine(userId);
    if (!existing) return;
    await setMachine(userId, { ...existing, lastSeenAt: timestamp });
  } catch {
    // Best-effort — telemetry doesn't depend on lastSeenAt freshness.
  }
}
